import { cn } from '@/lib/utils';
import { FormError } from '@chiroup/core/types/ErrorResponse.type';
import { QuestionMarkCircleIcon } from '@heroicons/react/24/outline';
import React, { useId } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { Tooltip } from '../Tooltip';
import { Switch } from '../ui/switch';
import { FieldErrors } from './FieldErrors';

type Props = {
  className?: string;
  errors?: FormError;
  label?: string | React.ReactNode;
  labelClassName?: string;
  tooltip?: string;
  tooltipClassName?: string;
  onChange?: (val: boolean) => void;
  value: boolean;
  labelOnLeft?: boolean;
  rightSide?: React.ReactNode;
  onValue?: string | number | boolean;
  hint?: string | React.ReactNode;
  disabled?: boolean;
  readonly?: boolean;
};

export const Toggle: React.FC<Props> = ({
  className,
  errors,
  label,
  labelClassName,
  tooltip,
  tooltipClassName,
  onChange,
  value,
  labelOnLeft,
  rightSide,
  onValue = true,
  hint,
  disabled = false,
  readonly = false,
}) => {
  const id = uuidv4();

  const tooltipIconClassName = 'h-3 w-3 text-gray-400 inline-block ml-2 align-text-top';
  const toolTipId = useId();

  const getLabelContents = () => {
    return (
      <div>
        <span className="inline-block">{label}</span>
        {tooltip && (
          <span className="inline-block">
            <Tooltip content={tooltip}>
              <QuestionMarkCircleIcon className={tooltipIconClassName} id={toolTipId} />
            </Tooltip>
          </span>
        )}
      </div>
    );
  };

  return (
    <div className={className}>
      <div className="flex flex-row">
        <div className="flex flex-col gap-2">
          <div
            className={[
              'flex items-center gap-2',
              labelOnLeft ? 'flex-row-reverse' : 'flex-row',
            ].join(' ')}
          >
            <Switch
              checked={value === onValue}
              onCheckedChange={checked => {
                if (!disabled && !readonly && typeof onChange === 'function') {
                  onChange(checked === true ? (onValue as boolean) : (!onValue as boolean));
                }
              }}
              disabled={disabled || readonly}
              className={cn(value === onValue && disabled ? 'opacity-50' : '')}
            />
            {!!label && (
              <label
                className={cn(
                  'block text-sm leading-5',
                  errors
                    ? 'text-red-500'
                    : disabled
                      ? 'text-gray-400 dark:text-dark-gray-100'
                      : 'text-gray-900 dark:text-dark-gray-200'
                )}
              >
                {getLabelContents()}
              </label>
            )}
          </div>
        </div>
        {rightSide}
        {hint && (
          <Tooltip content={hint}>
            <svg
              className="shrink-0 mr-1.5 h-5 w-5 text-gray-400 hover:text-gray-500"
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path
                fillRule="evenodd"
                d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                clipRule="evenodd"
              />
            </svg>
          </Tooltip>
        )}
        <div>
          <FieldErrors errors={errors} />
        </div>
      </div>
    </div>
  );
};
